import type { VariantProps } from "class-variance-authority"
import React from "react"
import { cva } from "class-variance-authority"

import { BodyBase } from "@/domains/design/components/body-base"
import { BodyCaption } from "@/domains/design/components/body-caption"
import { BodyLg } from "@/domains/design/components/body-lg"
import { BodySM } from "@/domains/design/components/body-sm"
import { BodyXs } from "@/domains/design/components/body-xs"
import { CheckmarkCircleIcon, InfoSquareIcon, WarningSquareIcon } from "@/domains/icons"
import { cn } from "@/domains/library"

import type { MessageBoxLayoutStrategy } from "../interfaces"
import { defaultLayoutStrategy } from "./message-box-layout-strategies"

const messageBoxVariants = cva("rounded-md border", {
  variants: {
    variant: {
      error: "border-error/50 bg-error/25 text-error-text",
      info: "border-p200-light bg-p300-light text-t300",
      scalest: "border-scalest-border/25 bg-scalest-background/25 text-scalest-text",
      success: "border-[#00B356]/25 bg-[#00B356]/10 text-[#00B356]",
    },
    size: {
      // ignore xxs for now - this will be extracted into a different component
      xxs: "max-h-[22px] gap-1 px-[4px] py-[1px] text-[10px]",

      // lets make them amazing across all screen sizes
      xs: "gap-2 px-3 py-2",
      sm: "gap-2 px-3 py-2",
      md: "gap-2 px-3 py-3",
      lg: "gap-2 px-3 py-3",
    },
    textShade: {
      error: "error",
      info: "info",
      scalest: "scalest",
      success: "success",
      t100: "t100",
      t200: "t200",
      t300: "t300",
    },
  },
  defaultVariants: {
    variant: "info",
    size: "sm",
    textShade: "t100",
  },
})

export interface MessageBoxProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof messageBoxVariants> {
  showIcon?: boolean
  layoutStrategy?: MessageBoxLayoutStrategy
}

function MessageBox({
  className,
  variant,
  size = "sm",
  children,
  showIcon = true,
  layoutStrategy,
  ...props
}: MessageBoxProps) {
  if (showIcon) {
    return (
      <MessageBoxWithIcon
        className={className}
        variant={variant}
        size={size}
        layoutStrategy={layoutStrategy}
        {...props}
      >
        {children}
      </MessageBoxWithIcon>
    )
  }
  return (
    <div className={cn("", messageBoxVariants({ variant, size }), className)} {...props}>
      {children}
    </div>
  )
}

function MessageBoxWithIcon({
  className,
  variant,
  size,
  textShade,
  children,
  layoutStrategy,
  ...props
}: MessageBoxProps) {
  const strategy = layoutStrategy ?? defaultLayoutStrategy

  let iconSize = 16
  let strokeWidth = 1.2
  switch (size) {
    case "xs":
      iconSize = 16
      break
    case "sm":
      iconSize = 18
      break
    case "md":
      iconSize = 22
      strokeWidth = 1.4
      break
    case "lg":
      iconSize = 24
      strokeWidth = 1.4
      break
  }

  let shade = "text-t100"
  switch (textShade) {
    case "error":
      shade = "text-error-text"
      break
    case "info":
      shade = "text-t200"
      break
    case "scalest":
      shade = "text-scalest-text"
      break
    case "success":
      shade = "text-success-text"
      break
    case undefined:
    case "t100":
      shade = "text-t100"
      break
    case "t200":
      shade = "text-t200"
      break
    case "t300":
      shade = "text-t300"
      break
    default:
      shade = "text-t100"
  }

  const icon = (
    <>
      {variant === "info" ? <InfoSquareIcon className="text-t200" strokeWidth={strokeWidth} size={iconSize} /> : null}
      {variant === "success" ? <CheckmarkCircleIcon className="text-[#00B356]" size={iconSize} /> : null}
      {variant === "error" ? <WarningSquareIcon size={iconSize} /> : null}
    </>
  )

  const content = (
    <>
      {size === "xxs" ? <BodyCaption className={shade}>{children}</BodyCaption> : null}
      {size === "xs" ? <BodyXs className={shade}>{children}</BodyXs> : null}
      {size === "sm" ? <BodySM className={shade}>{children}</BodySM> : null}
      {size === "md" ? <BodyBase className={shade}>{children}</BodyBase> : null}
      {size === "lg" ? <BodyLg className={shade}>{children}</BodyLg> : null}
    </>
  )

  const containerClassName = cn(messageBoxVariants({ variant, size }), className)

  return strategy.render(icon, content, containerClassName, props)
}

export { MessageBox, messageBoxVariants }
export { defaultLayoutStrategy, multilineLayoutStrategy } from "./message-box-layout-strategies"
export type { MessageBoxLayoutStrategy } from "../interfaces"
